/**
 * Multiplayer Chess Service
 * 
 * Handles multiplayer chess game functionality including:
 * - Private game creation with room codes
 * - Real-time move synchronization via WebSocket
 * - Connection management and reconnection
 * - Game state synchronization
 */

import { WebSocketService } from '../../services/WebSocketService';
import { ChessEngine } from '../ChessEngine';
import { Position, PieceType, PlayerColor, GameState } from '../types';

export interface RoomCode {
  code: string;
  expiresAt: number;
}

export interface MultiplayerGameConfig {
  mode: 'private';
  timeControl?: {
    initialTime: number;
    increment: number;
  };
  playerColor?: PlayerColor;
}

export interface MultiplayerMove {
  from: Position;
  to: Position;
  promotion?: PieceType;
  timestamp: number;
}

export interface ChessGameState {
  board: string[][];
  currentTurn: PlayerColor;
  moveHistory: any[];
  capturedPieces: {
    white: string[];
    black: string[];
  };
  castlingRights: {
    whiteKingSide: boolean;
    whiteQueenSide: boolean;
    blackKingSide: boolean;
    blackQueenSide: boolean;
  };
  enPassantTarget: { row: number; col: number } | null;
  halfMoveClock: number;
  fullMoveNumber: number;
  isCheck: boolean;
  isCheckmate: boolean;
  isStalemate: boolean;
  isDraw: boolean;
}

export class MultiplayerChessService {
  private wsService: WebSocketService;
  private engine: ChessEngine;
  private roomCode: string | null = null;
  private playerId: string | null = null;
  private playerColor: PlayerColor | null = null;
  private opponentConnected: boolean = false;
  private reconnecting: boolean = false;
  private reconnectAttempts: number = 0;
  private maxReconnectAttempts: number = 3;
  private isCreatingRoom: boolean = false; // Prevent multiple simultaneous creates
  private isJoiningRoom: boolean = false; // Prevent multiple simultaneous joins
  
  private onGameStartedCallback?: () => void;
  private onMoveUpdateCallback?: (move: any, gameState: ChessGameState) => void;
  private onGameOverCallback?: (result: string, winner: PlayerColor | null) => void;
  private onOpponentDisconnectedCallback?: () => void;
  private onOpponentReconnectedCallback?: () => void;
  private onGameStateReceivedCallback?: (gameState: ChessGameState) => void;
  private onErrorCallback?: (error: string) => void;
  private onConnectionStatusCallback?: (status: 'connected' | 'disconnected' | 'reconnecting') => void;

  constructor(wsService: WebSocketService, engine: ChessEngine) {
    this.wsService = wsService;
    this.engine = engine;
    this.playerId = `player_${Date.now()}_${Math.random().toString(36).substring(7)}`;
    this.setupMessageHandlers();
  }

  /**
   * Create a private game with a room code
   */
  async createRoom(): Promise<RoomCode> {
    try {
      // CRITICAL: Prevent creating if already in a room
      if (this.roomCode) {
        console.warn('⚠️ Already in room:', this.roomCode, 'Cannot create new room');
        // Return existing room instead of creating new one
        return {
          code: this.roomCode,
          expiresAt: Date.now() + 10 * 60 * 1000
        };
      }
      
      // CRITICAL: Prevent multiple simultaneous create attempts
      if (this.isCreatingRoom) {
        console.warn('⚠️ Room creation already in progress, waiting...');
        // Wait for the existing creation to complete
        return new Promise((resolve, reject) => {
          const checkInterval = setInterval(() => {
            if (!this.isCreatingRoom) {
              clearInterval(checkInterval);
              if (this.roomCode) {
                resolve({
                  code: this.roomCode,
                  expiresAt: Date.now() + 10 * 60 * 1000
                });
              } else {
                reject(new Error('Room creation failed'));
              }
            }
          }, 100);
          
          // Timeout after 10 seconds
          setTimeout(() => {
            clearInterval(checkInterval);
            reject(new Error('Room creation timeout'));
          }, 10000);
        });
      }
      
      this.isCreatingRoom = true;
      console.log('🔍 Starting room creation...');
      
      // Ensure WebSocket is connected first
      await this.wsService.connect();
      
      return new Promise((resolve, reject) => {
        const timeout = setTimeout(() => {
          this.isCreatingRoom = false; // Reset flag on timeout
          reject(new Error('Room creation timeout'));
        }, 10000);

        // Set up one-time handler for room creation response
        const originalCallback = this.onGameStartedCallback;
        this.onGameStartedCallback = () => {
          clearTimeout(timeout);
          this.isCreatingRoom = false; // Reset flag on success
          this.onGameStartedCallback = originalCallback; // Restore original
          
          if (this.roomCode && this.playerColor) {
            console.log(`✅ Room created successfully: ${this.roomCode}, playing as ${this.playerColor}`);
            resolve({
              code: this.roomCode,
              expiresAt: Date.now() + 10 * 60 * 1000
            });
            
            // Call original callback if it exists
            if (originalCallback) {
              originalCallback();
            }
          } else {
            reject(new Error('Room created but missing room code or color'));
          }
        };

        // Set up error handler
        const originalErrorCallback = this.onErrorCallback;
        this.onErrorCallback = (error: string) => {
          clearTimeout(timeout);
          this.isCreatingRoom = false; // Reset flag on error
          this.onGameStartedCallback = originalCallback;
          this.onErrorCallback = originalErrorCallback;
          reject(new Error(error));
        };

        console.log(`🔍 Creating room with playerId ${this.playerId}`);
        
        // Send create room message
        this.wsService.send({
          action: 'chess:createRoom',
          playerId: this.playerId
        });
      });
    } catch (error) {
      this.isCreatingRoom = false; // Reset flag on exception
      this.handleError('Failed to create room', error);
      throw error;
    }
  }

  /**
   * Join a private game using a room code
   */
  async joinRoom(roomCode: string): Promise<boolean> {
    try {
      if (!this.validateRoomCode(roomCode)) {
        throw new Error('Invalid room code format');
      }

      // CRITICAL: Prevent joining if already in a room
      if (this.roomCode) {
        console.warn('⚠️ Already in room:', this.roomCode, 'Cannot join:', roomCode);
        if (this.roomCode === roomCode) {
          console.log('✅ Already in this room, returning success');
          return true; // Already in this room, that's fine
        } else {
          throw new Error('Already in another room. Leave current room first.');
        }
      }
      
      // CRITICAL: Prevent multiple simultaneous join attempts
      if (this.isJoiningRoom) {
        console.warn('⚠️ Room join already in progress, rejecting duplicate');
        throw new Error('Room join already in progress');
      }
      
      this.isJoiningRoom = true;
      console.log('🔍 Starting room join...');

      // Ensure WebSocket is connected first
      await this.wsService.connect();

      return new Promise((resolve, reject) => {
        const timeout = setTimeout(() => {
          this.isJoiningRoom = false; // Reset flag on timeout
          reject(new Error('Room join timeout'));
        }, 10000);

        // Set up one-time handler for room join response
        const originalCallback = this.onGameStartedCallback;
        this.onGameStartedCallback = () => {
          clearTimeout(timeout);
          this.isJoiningRoom = false; // Reset flag on success
          this.onGameStartedCallback = originalCallback; // Restore original
          
          if (this.roomCode && this.playerColor) {
            console.log(`✅ Room joined successfully: ${this.roomCode}, playing as ${this.playerColor}`);
            resolve(true);
            
            // Call original callback if it exists
            if (originalCallback) {
              originalCallback();
            }
          } else {
            reject(new Error('Room joined but missing room code or color'));
          }
        };

        // Set up error handler
        const originalErrorCallback = this.onErrorCallback;
        this.onErrorCallback = (error: string) => {
          clearTimeout(timeout);
          this.isJoiningRoom = false; // Reset flag on error
          this.onGameStartedCallback = originalCallback;
          this.onErrorCallback = originalErrorCallback;
          reject(new Error(error));
        };

        console.log(`🔍 Attempting to join room ${roomCode} with playerId ${this.playerId}`);
        
        // Send join room message
        this.wsService.send({
          action: 'chess:joinRoom',
          roomCode,
          playerId: this.playerId
        });
      });
    } catch (error) {
      this.isJoiningRoom = false; // Reset flag on exception
      this.handleError('Failed to join room', error);
      throw error;
    }
  }

  /**
   * Send a move to the opponent
   */
  async sendMove(from: Position, to: Position, promotion?: PieceType, gameState?: ChessGameState): Promise<boolean> {
    try {
      if (!this.roomCode) {
        throw new Error('Not in a multiplayer game');
      }

      // Convert Position {rank, file} to server format {row, col}
      // rank 0 = row 7 (bottom of board from white's perspective)
      // rank 7 = row 0 (top of board from white's perspective)
      const move = {
        from: { row: 7 - from.rank, col: from.file },
        to: { row: 7 - to.rank, col: to.file },
        promotion
      };

      console.log('📤 Sending move to server:', {
        roomCode: this.roomCode,
        playerId: this.playerId,
        move,
        gameState,
        originalFrom: from,
        originalTo: to
      });

      this.wsService.send({
        action: 'chess:move',
        roomCode: this.roomCode,
        playerId: this.playerId,
        move,
        gameState
      });

      console.log('✅ Move sent successfully');
      return true;
    } catch (error) {
      console.error('❌ Failed to send move:', error);
      this.handleError('Failed to send move', error);
      return false;
    }
  }

  /**
   * Resign from the current game
   */
  async resign(): Promise<void> {
    try {
      if (!this.roomCode) {
        throw new Error('Not in a multiplayer game');
      }

      this.wsService.send({
        action: 'chess:resign',
        roomCode: this.roomCode,
        playerId: this.playerId
      });
    } catch (error) {
      this.handleError('Failed to resign', error);
    }
  }

  /**
   * Offer a draw
   */
  async offerDraw(): Promise<void> {
    try {
      if (!this.roomCode) {
        throw new Error('Not in a multiplayer game');
      }

      this.wsService.send({
        action: 'chess:offerDraw',
        roomCode: this.roomCode,
        playerId: this.playerId
      });
    } catch (error) {
      this.handleError('Failed to offer draw', error);
    }
  }

  /**
   * Accept a draw offer
   */
  async acceptDraw(): Promise<void> {
    try {
      if (!this.roomCode) {
        throw new Error('Not in a multiplayer game');
      }

      this.wsService.send({
        action: 'chess:acceptDraw',
        roomCode: this.roomCode,
        playerId: this.playerId
      });
    } catch (error) {
      this.handleError('Failed to accept draw', error);
    }
  }

  /**
   * Handle connection loss and attempt reconnection
   */
  async handleDisconnect(): Promise<void> {
    this.reconnecting = true;
    this.opponentConnected = false;
    
    if (this.onConnectionStatusCallback) {
      this.onConnectionStatusCallback('disconnected');
    }
    
    if (this.onOpponentDisconnectedCallback) {
      this.onOpponentDisconnectedCallback();
    }

    // Attempt reconnection with exponential backoff
    this.attemptReconnection();
  }

  /**
   * Attempt reconnection with exponential backoff
   */
  private async attemptReconnection(): Promise<void> {
    if (this.reconnectAttempts >= this.maxReconnectAttempts) {
      this.handleError('Max reconnection attempts reached');
      return;
    }

    const delay = Math.pow(2, this.reconnectAttempts) * 1000; // 1s, 2s, 4s
    this.reconnectAttempts++;

    if (this.onConnectionStatusCallback) {
      this.onConnectionStatusCallback('reconnecting');
    }

    setTimeout(async () => {
      try {
        if (this.roomCode && this.playerId) {
          this.wsService.send({
            action: 'chess:reconnect',
            roomCode: this.roomCode,
            playerId: this.playerId
          });
        }
      } catch (error) {
        this.attemptReconnection();
      }
    }, delay);
  }

  /**
   * Handle successful reconnection
   */
  async handleReconnect(): Promise<void> {
    this.reconnecting = false;
    this.reconnectAttempts = 0;
    
    if (this.onConnectionStatusCallback) {
      this.onConnectionStatusCallback('connected');
    }
  }

  /**
   * Leave the current game
   */
  async leaveGame(): Promise<void> {
    try {
      this.cleanup();
    } catch (error) {
      this.handleError('Failed to leave game', error);
    }
  }

  /**
   * Validate room code format
   */
  private validateRoomCode(code: string): boolean {
    return /^[A-Z0-9]{6}$/.test(code);
  }

  /**
   * Setup WebSocket message handlers
   */
  private setupMessageHandlers(): void {
    // Set custom message handler for chess messages
    this.wsService.setCustomMessageHandler((message: any) => {
      try {
        console.log('♟️ Chess message received:', message.type);
        
        // Handle chess-specific messages
        switch (message.type) {
          case 'chess:roomCreated':
            // Store room code and color from server
            // Room creator always plays as white (first player)
            this.roomCode = message.roomCode;
            this.playerColor = (message.color as PlayerColor) || 'white';
            console.log(`♟️ Room created: ${this.roomCode}, playing as ${this.playerColor}`);
            console.log('🎨 CLIENT RECEIVED roomCreated:', {
              roomCode: message.roomCode,
              color: message.color,
              storedColor: this.playerColor
            });
            
            if (this.onGameStartedCallback) {
              this.onGameStartedCallback();
            }
            break;

          case 'chess:roomJoined':
            // Store room code and color from server
            // Room joiner always plays as black (second player)
            this.roomCode = message.roomCode;
            this.playerColor = (message.color as PlayerColor) || 'black';
            console.log(`♟️ Room joined: ${this.roomCode}, playing as ${this.playerColor}`);
            console.log('🎨 CLIENT RECEIVED roomJoined:', {
              roomCode: message.roomCode,
              color: message.color,
              storedColor: this.playerColor
            });
            
            if (this.onGameStartedCallback) {
              this.onGameStartedCallback();
            }
            break;

          case 'chess:gameStarted':
            if (this.onGameStartedCallback) {
              this.onGameStartedCallback();
            }
            break;

          case 'chess:opponentJoined':
            // Opponent has joined the room — game can start
            console.log(`♟️ Opponent joined room: ${message.roomCode}`);
            // Trigger game started since both players are now connected
            if (this.onGameStartedCallback) {
              this.onGameStartedCallback();
            }
            break;

          case 'chess:moveUpdate':
            if (this.onMoveUpdateCallback) {
              this.onMoveUpdateCallback(message.move, message.gameState);
            }
            break;

          case 'chess:gameOver':
            if (this.onGameOverCallback) {
              this.onGameOverCallback(message.result, message.winner);
            }
            break;

          case 'chess:playerDisconnected':
            if (this.onOpponentDisconnectedCallback) {
              this.onOpponentDisconnectedCallback();
            }
            break;

          case 'chess:playerReconnected':
            if (this.onOpponentReconnectedCallback) {
              this.onOpponentReconnectedCallback();
            }
            break;

          case 'chess:gameState':
            if (this.onGameStateReceivedCallback) {
              this.onGameStateReceivedCallback(message.gameState);
            }
            this.handleReconnect();
            break;

          case 'chess:error':
            this.handleError(message.error);
            break;
        }
      } catch (error) {
        console.error('Error handling chess message:', error);
        this.handleError('Message handling error', error);
      }
    });
  }

  /**
   * Handle errors
   */
  private handleError(message: string, error?: any): void {
    console.error(`MultiplayerChessService: ${message}`, error);
    if (this.onErrorCallback) {
      this.onErrorCallback(message);
    }
  }

  /**
   * Cleanup resources
   */
  private cleanup(): void {
    this.roomCode = null;
    this.playerColor = null;
    this.opponentConnected = false;
    this.reconnecting = false;
    this.reconnectAttempts = 0;
  }

  // Event handlers
  onGameStarted(callback: () => void): void {
    this.onGameStartedCallback = callback;
  }

  onMoveUpdate(callback: (move: any, gameState: ChessGameState) => void): void {
    this.onMoveUpdateCallback = callback;
  }

  onGameOver(callback: (result: string, winner: PlayerColor | null) => void): void {
    this.onGameOverCallback = callback;
  }

  onOpponentDisconnected(callback: () => void): void {
    this.onOpponentDisconnectedCallback = callback;
  }

  onOpponentReconnected(callback: () => void): void {
    this.onOpponentReconnectedCallback = callback;
  }

  onGameStateReceived(callback: (gameState: ChessGameState) => void): void {
    this.onGameStateReceivedCallback = callback;
  }

  onError(callback: (error: string) => void): void {
    this.onErrorCallback = callback;
  }

  onConnectionStatus(callback: (status: 'connected' | 'disconnected' | 'reconnecting') => void): void {
    this.onConnectionStatusCallback = callback;
  }

  // Getters
  getRoomCode(): string | null {
    return this.roomCode;
  }

  getPlayerColor(): PlayerColor | null {
    return this.playerColor;
  }

  isOpponentConnected(): boolean {
    return this.opponentConnected;
  }

  isReconnecting(): boolean {
    return this.reconnecting;
  }

  isMyTurn(currentTurn: PlayerColor): boolean {
    return this.playerColor === currentTurn;
  }
}
